import api from './axiosConfig';

export type LiveSummaryTone = 'up' | 'down' | 'neutral' | 'warn' | 'good';
export type LiveSummarySourceStatus = 'idle' | 'ok' | 'partial' | 'error';
export type LiveSummaryTab = 'SUMMARY' | 'SECTOR' | 'THEME' | 'STOCK' | 'HOLDING' | 'ALERT';

export type LiveSummaryAccess = {
  authenticated?: boolean;
  defaultTab?: LiveSummaryTab | string;
  publicTabs?: Array<LiveSummaryTab | string>;
  lockedTabs?: Array<LiveSummaryTab | string>;
  stockDetailLocked?: boolean;
  holdingLocked?: boolean;
  alertLocked?: boolean;
  loginRequired?: boolean;
  loginMessage?: string;
};

export type LiveSummaryInvestorTrendDirection =
    | 'UP_STREAK' | 'DOWN_STREAK' | 'UP_TO_DOWN' | 'DOWN_TO_UP'
    | 'FLAT' | 'INSUFFICIENT_DATA' | 'DATA_STALE';

export type LiveSummaryMarketState = {
  label?: string; score?: number | null; tone?: LiveSummaryTone | string;
  phase?: string; baseDate?: string; generatedAt?: string;
  topEtfName?: string; topEtfTicker?: string; topSectorCode?: string;
  topScore?: number | null; comment?: string;
};

export type LiveSummaryLiveIssueSignalStock = {
  ticker?: string;
  name?: string;
  relation?: 'DIRECT' | 'POSSIBLE' | string;
};

export type LiveSummaryLiveIssueBriefAggregate = {
  state?: 'NEW' | 'STRENGTHENED' | 'WEAKENED' | 'RESOLVED' | 'NONE' | string;
  direction?: 'POSITIVE' | 'NEGATIVE' | 'MIXED' | 'NEUTRAL' | 'UNKNOWN' | string;
  summary?: string;
  evidenceLevel?: 'CONFIRMED' | 'REPORTED' | 'UNVERIFIED' | string;
  evidenceRefs?: string[];
};

export type LiveSummarySectorImpact = {
  sectorName?: string;
  industryCode?: string;
  marketType?: string;
  changeRate?: number | null;
  mappedTickerCount?: number | null;
  realtimeTickerCount?: number | null;
  upTickerCount?: number | null;
  downTickerCount?: number | null;
  breadthPct?: number | null;
  realtimeCoveragePct?: number | null;
  realtimeTradingValue?: number | null;
  latestRealtimeEventAt?: string;
  dataStatus?: 'GOOD' | 'PARTIAL' | 'NO_DATA' | string;
  classification?: 'DIRECT_CATALYST' | 'SUPPORTING_FACTOR' | 'DIRECT_PRESSURE' | 'PRESSURE_FACTOR' | 'CO_MOVE' | 'MIXED' | 'UNCONFIRMED' | string;
  label?: string;
  reason?: string;
};

export type LiveSummaryLiveIssueBriefSector = {
  name?: string;
  direction?: 'POSITIVE' | 'NEGATIVE' | 'MIXED' | 'NEUTRAL' | 'UNKNOWN' | string;
  summary?: string;
  evidenceLevel?: 'CONFIRMED' | 'REPORTED' | 'UNVERIFIED' | string;
  evidenceRefs?: string[];
  sectorImpact?: LiveSummarySectorImpact | null;
};

export type LiveSummaryLiveIssueBriefStock = {
  ticker?: string;
  name?: string;
  relation?: 'DIRECT' | 'POSSIBLE' | string;
  direction?: 'POSITIVE' | 'NEGATIVE' | 'MIXED' | 'NEUTRAL' | 'UNKNOWN' | string;
  summary?: string;
  evidenceLevel?: 'CONFIRMED' | 'REPORTED' | 'UNVERIFIED' | string;
  evidenceRefs?: string[];
};

export type LiveSummaryLiveIssueBriefCatalyst = {
  headline?: string;
  scope?: 'MARKET' | 'SECTOR' | 'THEME' | 'STOCK' | 'MACRO' | string;
  direction?: 'POSITIVE' | 'NEGATIVE' | 'MIXED' | 'NEUTRAL' | 'UNKNOWN' | string;
  summary?: string;
  evidenceLevel?: 'CONFIRMED' | 'REPORTED' | 'UNVERIFIED' | string;
  evidenceRefs?: string[];
};

export type LiveSummaryLiveIssueBrief = {
  overview?: string;
  evidenceRefs?: string[];
  policy?: LiveSummaryLiveIssueBriefAggregate;
  market?: LiveSummaryLiveIssueBriefAggregate;
  sectors?: LiveSummaryLiveIssueBriefSector[];
  stocks?: LiveSummaryLiveIssueBriefStock[];
  catalysts?: {
    born?: LiveSummaryLiveIssueBriefCatalyst[];
    strengthened?: LiveSummaryLiveIssueBriefCatalyst[];
    weakened?: LiveSummaryLiveIssueBriefCatalyst[];
    expired?: LiveSummaryLiveIssueBriefCatalyst[];
  };
  watch?: Array<{ text?: string; evidenceLevel?: string; evidenceRefs?: string[] }>;
};

export type LiveSummaryLiveIssueSignalItem = {
  changeType?: 'NEW' | 'STRENGTHENED' | 'WEAKENED' | 'RESOLVED' | string;
  scope?: 'MARKET' | 'SECTOR' | 'THEME' | 'STOCK' | 'MACRO' | string;
  eventType?: string;
  headline?: string;
  direction?: 'POSITIVE' | 'NEGATIVE' | 'MIXED' | 'UNKNOWN' | string;
  importance?: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  evidenceLevel?: 'CONFIRMED' | 'REPORTED' | 'UNVERIFIED' | string;
  sectors?: string[];
  themes?: string[];
  stocks?: LiveSummaryLiveIssueSignalStock[];
  reason?: string;
  watch?: string;
  evidenceRefs?: string[];
  sectorImpacts?: LiveSummarySectorImpact[];
};

/** Python 10-minute P1 shadow evidence. This is NOT ActionDecision authority. */
export type LiveSummaryP1Shadow = {
  schema_version?: string;
  mode?: string;
  release_gate?: string;
  window_start?: string;
  window_end?: string;
  market?: { state?: string; regime?: string; forecast_status?: string };
  realtime_observation?: {
    source?: string;
    captured_at?: string;
    index_status?: string;
    stocks_status?: string;
    history_status?: string;
    history_sample_count?: number;
    sampled_after_window_end_sec?: number;
    sampled_index_changes?: Record<string, Record<string, { status?: string; return_pct?: number | null }>>;
    sampled_stock_changes?: Record<string, unknown>;
  };
  reason_codes?: string[];
  top_changes?: unknown[];
  decisions?: unknown[];
};

export type LiveSummaryLiveIssueSignal = {
  p1_shadow?: LiveSummaryP1Shadow | null;
  contractVersion?: string;
  briefVersion?: string;
  status?: 'NO_DATA' | 'NO_SIGNAL' | 'SIGNAL' | string;
  window?: {
    start?: string;
    end?: string;
    sourceMessageCount?: number | null;
    candidateMessageCount?: number | null;
  };
  summary?: {
    newSignalCount?: number | null;
    highImportanceCount?: number | null;
  };
  brief?: LiveSummaryLiveIssueBrief;
  signals?: LiveSummaryLiveIssueSignalItem[];
  capitalScenario?: Record<string, unknown>;
  reason?: string;
};

export type LiveSummaryIndexPoint = {
  label?: string;
  value?: number | null;
  changeValue?: number | null;
  changeRate?: number | null;
  updatedAt?: string;
  delayMinutes?: number | null;
  stale?: boolean | number | null;
  provider?: string;
};

export type LiveSummaryDerivativePoint = {
  label?: string;
  futuresPrice?: number | null;
  futuresChangeRate?: number | null;
  marketBasis?: number | null;
  basisChange5m?: number | null;
  basisChange15m?: number | null;
  oiChangeCumulative?: number | null;
  oiChange5m?: number | null;
  updatedAt?: string;
  delayMinutes?: number | null;
  stale?: boolean | number | null;

  /** Legacy price/OI structure is preserved for compatibility only. */
  structure?: string;
  structureLabel?: string;

  /** Non-learning direction model: futures lead + Basis sign confluence. */
  directionState?: string;
  directionLabel?: string;
  directionDataStatus?: string;
  futuresReturn5m?: number | null;
  futuresReturn15m?: number | null;
  spotReturn5m?: number | null;
  spotReturn15m?: number | null;
  futuresLead5m?: number | null;
  futuresLead15m?: number | null;
  oiState?: string;
  oi5mState?: string;
  foreignNetContracts?: number | null;
  institutionNetContracts?: number | null;
  foreignAlignment?: string;
  participantFlowObservedAt?: string;
  provider?: string;
};

export type LiveSummaryGlobalPoint = {
  label?: string;
  last?: number | null;
  changeRate?: number | null;
  provider?: string;
};

export type LiveSummaryMetricGuide = {
  title?: string;
  summary?: string;
  action?: string;
  tone?: LiveSummaryTone | string;
};

export type LiveSummaryMarketContext = {
  available?: boolean | number | null;
  dataStatus?: string;
  updatedAt?: string;
  stale?: boolean | number | null;
  headline?: string;
  description?: string;
  action?: string;
  tone?: LiveSummaryTone | string;
  alertBadge?: string;
  transitionActive?: boolean | number | null;
  transitionHeadline?: string;
  transitionDescription?: string;
  transitionTone?: LiveSummaryTone | string;
  marketPressureScore?: number | null;
  flowQualityScore?: number | null;
  entryRiskScore?: number | null;
  reversalWatchScore?: number | null;
  positionFlowScore?: number | null;
  basisFlowScore?: number | null;
  confidenceScore?: number | null;
  confidenceLabel?: string;
  pressureLabel?: string;
  positionFlowLabel?: string;
  basisFlowLabel?: string;
  qualityLabel?: string;
  riskLabel?: string;
  watchLabel?: string;
  checkpoints?: string[];

  /** kor_stock_real_time 통합 장중 모델 (-100..100). */
  intradayScore?: number | null;
  intradayDirection?: string;
  intradayConfidence?: string;
  intradayCoveragePct?: number | null;
  riskRealtimeScore?: number | null;

  /** 국내 시장은 KIS authoritative source만 사용합니다. */
  domesticIndices?: Partial<Record<'KOSPI' | 'KOSDAQ' | 'KOSPI200' | 'KOSDAQ150', LiveSummaryIndexPoint>>;
  derivatives?: Partial<Record<'KOSPI200' | 'KOSDAQ150', LiveSummaryDerivativePoint>>;

  /** 해외 시장/환율은 글로벌 overlay 용도입니다. */
  globalMarkets?: Partial<Record<'USDKRW' | 'DXY' | 'NQ' | 'ES' | 'YM' | 'RTY' | 'US10Y_FUT' | 'VIX', LiveSummaryGlobalPoint>>;
  sourcePolicy?: Record<string, string>;
  metricGuides?: Record<string, LiveSummaryMetricGuide>;
};

/** @deprecated 신규 UI는 marketContext.derivatives를 사용합니다. */
export type LiveSummaryDerivativeSignal = {
  available?: boolean | number | null; dataStatus?: string; contractCode?: string;
  contractName?: string; tradeDate?: string; minuteAt?: string; lastEventAt?: string;
  futuresPrice?: number | null; futuresChange?: number | null; futuresChangeRate?: number | null;
  futuresOpen?: number | null; futuresHigh?: number | null; futuresLow?: number | null;
  marketBasis?: number | null; basisChange1m?: number | null; basisChange5m?: number | null;
  basisChange15m?: number | null; openInterest?: number | null; sessionOpenInterest?: number | null;
  oiChangeCumulative?: number | null; oiChange1m?: number | null; oiChange5m?: number | null;
  oiChange15m?: number | null; oiBaselineSource?: string;
  direction?: string; directionLabel?: string;
  structure?: string; structureLabel?: string; score?: number | null;
  directionState?: string; directionDataStatus?: string;
  futuresReturn5m?: number | null; futuresReturn15m?: number | null;
  spotReturn5m?: number | null; spotReturn15m?: number | null;
  futuresLead5m?: number | null; futuresLead15m?: number | null;
  oiState?: string; oi5mState?: string;
  foreignNetContracts?: number | null; institutionNetContracts?: number | null;
  foreignAlignment?: string; participantFlowObservedAt?: string;
  confidenceScore?: number | null; confidence?: string; tone?: LiveSummaryTone | string;
  comment?: string; delayMinutes?: number | null; stale?: boolean | number | null;
  detailLocked?: boolean | number | null; accessLevel?: string;
};

export type LiveSummaryInvestorFlow = {
  programMatched?: boolean | number | null;
  programNetBuyExecAmount?: number | null;
  programNetBuyToTradingValuePct?: number | null;
  programReceivedAt?: string;
  investorMatched?: boolean | number | null;
  investorAggregateMatched?: boolean | number | null;
  investorEstimateMatched?: boolean | number | null;
  investorFlowStatus?: string;
  foreignNetBuyAmount?: number | null;
  institutionNetBuyAmount?: number | null;
  smartMoneyNetBuyAmount?: number | null;
  estimateForeignNetBuyQty?: number | null;
  estimateInstitutionNetBuyQty?: number | null;
  estimateSmartMoneyNetBuyQty?: number | null;
  investorEstimateBucket?: string;
  investorAggregateEventAt?: string;
  investorEstimateEventAt?: string;
  investorReceivedAt?: string;
  investorTrendDirection?: LiveSummaryInvestorTrendDirection | string;
  investorTrendStreakMinutes?: number | null;
  investorTrendDelta1m?: number | null;
  investorTrendDelta5m?: number | null;
  /** 직전 최대 10개 1분 수급 변화량의 절대값 평균. 최소 5개 기준점이 있을 때 velocity 계산에 사용. */
  investorTrendBaseline1m?: number | null;
  /** 현재 1분 수급 변화 속도 / 직전 최대 10분 baseline. 데이터 부족 시 null. */
  investorTrendVelocityRatio?: number | null;
  /** 현재 1분 변화 속도 / 최근 5분 평균 변화 속도. 데이터 부족 시 null. */
  investorTrendAccelerationRatio?: number | null;
  investorTrendBaselinePointCount?: number | null;
  investorTrendTurnedAt?: string;
  investorTrendUpdatedAt?: string;
  investorTrendLeader?: 'FOREIGN' | 'INSTITUTION' | 'BALANCED' | string;
  investorTrendConfidence?: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  investorTrendPointCount?: number | null;
  flowDecision?: string;
  flowDecisionTone?: LiveSummaryTone | string;
  flowReason?: string;
};

export type StockValueJudgmentLayer = {
  state?: string;
  label?: string;
  reasons?: string[];
};

export type StockValueJudgmentBusinessEvidence = {
  ready?: boolean;
  stale?: boolean;
  needsReview?: boolean;
  financialIndustry?: boolean;
  state?: string;
  summary?: string;
  businessDate?: string;
  reportName?: string;
  industry?: string;
  subIndustry?: string;
  reportShape?: string;
  semanticStatus?: string;
  semanticConfidence?: string;
  profileReadinessScore?: number | null;
  consistencyIssueCount?: number | null;
};

export type StockValueJudgment = {
  contractVersion?: string;
  ticker?: string;
  basisMode?: 'EOD' | 'LIVE' | string;
  basisDate?: string;
  valueState?: 'STRENGTHENED' | 'PRESERVED' | 'SHAKEN' | 'DAMAGED' | 'LIMITED' | string;
  valueStateLabel?: string;
  valueStateTone?: LiveSummaryTone | string;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  confidenceLabel?: string;
  changed?: boolean | number | null;
  summary?: string;
  reasons?: string[];
  nextChecks?: string[];
  recentValueChanges?: string[];
  recentMarketChanges?: string[];
  /** @deprecated compatibility aggregate; prefer the separated value/market lists. */
  recentEvidenceChanges?: string[];
  businessEvidence?: StockValueJudgmentBusinessEvidence;
  fundamental?: StockValueJudgmentLayer;
  thesis?: StockValueJudgmentLayer;
  marketDelivery?: StockValueJudgmentLayer;
  dataQuality?: Record<string, unknown>;
};

export type StockEntryDecisionState = 'FAVORABLE' | 'CONDITIONAL' | 'RISK_DOMINANT' | 'LIMITED' | string;

export type StockEntryDecisionEvidence = {
  domain?: 'COMPANY' | 'PRICE_FLOW' | 'MARKET' | 'SCANNER' | string;
  state?: 'CONFIRMED' | 'OBSERVED' | 'CHECKING' | 'LIMITED' | string;
  label?: string;
  reason?: string;
  tone?: LiveSummaryTone | string;
};

export type StockEntryDecision = {
  contractVersion?: string;
  available?: boolean | number | null;
  state?: StockEntryDecisionState;
  label?: string;
  tone?: LiveSummaryTone | string;
  summary?: string;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW' | string;
  evidence?: StockEntryDecisionEvidence[];
  confirmationConditions?: string[];
  riskConditions?: string[];
  nextCheck?: string;
  recentMaterialChanges?: string[];
  asOf?: string;
};

export type LiveSummarySessionPrice = {
  openPrice?: number | null;
  highPrice?: number | null;
  lowPrice?: number | null;
  sessionVwap?: number | null;
  sessionDate?: string;
  lastEventAt?: string;
};

/** @deprecated 기존 import 호환용. 신규 UI에서는 세션 가격 데이터만 사용합니다. */
export type LiveSummaryTradePlan = LiveSummarySessionPrice;

export type LiveSummarySectorTopStock = LiveSummaryInvestorFlow & LiveSummarySessionPrice & {
  ticker?: string; name?: string; changeRate?: number | null; price?: number | null;
  tradingValue?: number | null; volume?: number | null; realtimeMatched?: boolean | number | null;
  dataSource?: string; priceAsOf?: string; isRealtime?: boolean | number | null;
  delayMinutes?: number | null; tone?: LiveSummaryTone | string;
  valueJudgment?: StockValueJudgment | null;
  entryDecision?: StockEntryDecision | null;
};

export type LiveSummarySector = {
  name?: string; sectorCode?: string; marketType?: string; marketLabel?: string;
  industryCode?: string; industryName?: string; changeRate?: number | null;
  tone?: LiveSummaryTone | string; mappedTickerCount?: number | null;
  realtimeTickerCount?: number | null; upTickerCount?: number | null;
  downTickerCount?: number | null; realtimeCoveragePct?: number | null;
  realtimeTradingValue?: number | null; latestRealtimeEventAt?: string;
  dataSource?: string; isRealtime?: boolean | number | null; delayMinutes?: number | null;
  topStocks?: LiveSummarySectorTopStock[]; topStockCount?: number | null;
  stockDetailLocked?: boolean | number | null; accessLevel?: string;
};

export type LiveSummaryThemeFlow = {
  themeCode?: string;
  themeName?: string;
  sessionDate?: string;
  constituentCount?: number | null;
  priceAvailableCount?: number | null;
  flowAvailableCount?: number | null;
  programAvailableCount?: number | null;
  avgChangeRate?: number | null;
  foreignNetBuyAmount?: number | null;
  institutionNetBuyAmount?: number | null;
  smartMoneyNetBuyAmount?: number | null;
  programNetBuyAmount?: number | null;
  netBuyStockCount?: number | null;
  netSellStockCount?: number | null;
  flatStockCount?: number | null;
  smartMoneyBreadthPct?: number | null;
  flowCoveragePct?: number | null;
  priceCoveragePct?: number | null;
  topSmartMoneyTicker?: string;
  topSmartMoneyCorpName?: string;
  topSmartMoneyAmount?: number | null;
  sourceLatestAt?: string;
  dataStatus?: 'GOOD' | 'PARTIAL' | 'NO_DATA' | string;
  receivedAt?: string;
};

export type LiveSummaryThemeStock = {
  themeCode?: string;
  themeName?: string;
  sessionDate?: string;
  ticker?: string;
  name?: string;
  price?: number | null;
  changeRate?: number | null;
  tradingValue?: number | null;
  market?: string;
  themeChangeRate?: number | null;
  themePriceCoveragePct?: number | null;
  themeDataStatus?: 'GOOD' | 'PARTIAL' | 'NO_DATA' | string;
  foreignNetBuyAmount?: number | null;
  institutionNetBuyAmount?: number | null;
  smartMoneyNetBuyAmount?: number | null;
  programNetBuyAmount?: number | null;
  priceAvailable?: boolean | number | null;
  flowAvailable?: boolean | number | null;
  programAvailable?: boolean | number | null;
  dataStatus?: 'GOOD' | 'PARTIAL' | 'NO_DATA' | string;
  lastEventAt?: string;
  investorAggregateEventAt?: string;
  investorEstimateEventAt?: string;
  programReceivedAt?: string;
  sourceLatestAt?: string;
};


export type LiveSummaryHoldingThesis = {
  contractVersion?: 'HOLDING_THESIS_V1' | string;
  available?: boolean | number | null;
  coreState?: 'PRESERVED' | 'WEAKENED' | 'DAMAGED' | 'LIMITED' | string;
  coreLabel?: string;
  displayState?: 'PRESERVED' | 'PRESERVED_CAUTION' | 'WEAKENED' | 'DAMAGED' | 'LIMITED' | string;
  displayLabel?: string;
  tone?: LiveSummaryTone | string;
  marketPressureState?: 'DOWN_STRONG' | 'DOWN' | 'NEUTRAL' | 'UP' | 'UP_STRONG' | 'UNKNOWN' | string;
  marketPressureLabel?: string;
  marketPressureTone?: LiveSummaryTone | string;
  marketPressureScore?: number | null;
  marketOnlyPressure?: boolean | number | null;
  pressureOnly?: boolean | number | null;
  fundamentalState?: string;
  fundamentalLabel?: string;
  thesisState?: string;
  thesisLabel?: string;
  verifiedBaselineAvailable?: boolean | number | null;
  verifiedBaselineLabel?: string;
  newEvidenceConflict?: boolean | number | null;
  contextIssuePending?: boolean | number | null;
  contextIssueHeadline?: string;
  calculationMode?: 'KRX_REALTIME' | 'NXT_REALTIME' | 'KRX_CLOSE_TRANSITION' | 'EOD_DAILY' | 'PREMARKET_DAILY' | 'WEEKEND_DAILY' | string;
  marketPressureBasis?: 'KRX_REALTIME' | 'KRX_CLOSE_CONTEXT' | 'LAST_KRX_CLOSE' | string;
  marketSession?: string;
  calculatedAt?: string;
  referenceDate?: string;
  eodFinalized?: boolean | number | null;
  weekendDaily?: boolean | number | null;
  reasons?: string[];
  todayChanges?: string[];
  invalidationConditions?: string[];
  recheckConditions?: string[];
  monitorConditions?: string[];
  monitorConditionCount?: number | null;
  triggerSignals?: string[];
  triggeredCount?: number | null;
  damageConfirmed?: boolean | number | null;
  weakeningConfirmed?: boolean | number | null;
  flowTone?: string;
  flowLabel?: string;
  basisDate?: string;
};

export type LiveSummaryHolding = LiveSummaryInvestorFlow & LiveSummarySessionPrice & {
  ticker?: string; name?: string; changeRate?: number | null; price?: number | null;
  tradingValue?: number | null; dataSource?: string; priceAsOf?: string;
  isRealtime?: boolean | number | null; delayMinutes?: number | null;
  status?: '위험중' | '관찰' | '양호' | string; statusTone?: LiveSummaryTone | string;
  valueJudgment?: StockValueJudgment | null;
  holdingThesis?: LiveSummaryHoldingThesis | null;

  /**
   * LiveSummary server-side judgment authorities.
   * Detailed normalization remains in LiveSummaryDock.
   */
  actionDecision?: Record<string, unknown> | null;
  riskAssessment?: Record<string, unknown> | null;
  judgmentContract?: Record<string, unknown> | null;
  judgment_contract?: Record<string, unknown> | null;
};

export type LiveSummaryAlert = {
  level?: 'info' | 'warning' | 'danger' | string;
  message?: string;
};

export type LiveSummaryOpportunity = {
  ticker?: string;
  name?: string;
  marketLabel?: string;
  sectorName?: string;
  price?: number | null;
  changeRate?: number | null;
  tradingValue?: number | null;
  opportunityScore?: number | null;
  riskPenalty?: number | null;
  category?: string;
  tone?: LiveSummaryTone | string;
  marketReady?: boolean | number | null;
  marketFitScore?: number | null;
  marketFitLabel?: string;
  marketFitTone?: LiveSummaryTone | string;
  sectorChangeRate?: number | null;
  sectorBreadthPct?: number | null;
  vwapGapPct?: number | null;
  smartMoneyNetBuyAmount?: number | null;
  programNetBuyToTradingValuePct?: number | null;
  investorTrendDirection?: LiveSummaryInvestorTrendDirection | string;
  reasons?: string[];
  primaryReason?: string;
  warning?: string;
  detailLocked?: boolean | number | null;
  accessLevel?: string;
};

export type LiveSummaryAttentionBanner = {
  visible?: boolean | number | null;
  severity?: 'INFO' | 'WATCH' | 'HIGH' | 'CRITICAL' | string;
  title?: string;
  message?: string;
  ticker?: string;
  name?: string;
  actionTab?: 'ALERTS' | string;
  tone?: LiveSummaryTone | string;
};

export type LiveSummarySignalHorizonStats = {
  sampleCount?: number;
  available?: boolean;
  avgReturnPct?: number | null;
  riseRatePct?: number | null;
  fallRatePct?: number | null;
};

export type LiveSummarySignalHistoryStats = {
  ticker?: string;
  eventType?: string;
  eventLabel?: string;
  scope?: 'TICKER' | 'PUBLIC' | string;
  scopeLabel?: string;
  lookbackDays?: number;
  minimumSample?: number;
  eventCount?: number;
  usableSampleCount?: number;
  available?: boolean;
  horizons?: Record<string, LiveSummarySignalHorizonStats>;
};

export type LiveSummaryChangeEvent = {
  id?: number | string;
  eventScope?: 'PUBLIC' | 'USER' | string;
  entityType?: 'SECTOR' | 'STOCK' | 'HOLDING' | 'ISSUE' | string;
  entityKey?: string;
  eventType?: string;
  severity?: 'INFO' | 'WARNING' | 'DANGER' | string;
  title?: string;
  message?: string;
  previousValue?: string | null;
  currentValue?: string | null;
  changeValue?: number | null;
  payload?: Record<string, unknown> | string | null;
  sourceEventAt?: string;
  detectedAt?: string;
};

export type LiveSummaryChangeLayer =
    | 'FACT_CHANGE'
    | 'INTERPRETATION_CHANGE'
    | 'JUDGMENT_CHANGE'
    | string;

export type LiveSummaryChangeItem = {
  eventId?: number | string;
  changeType?: string;
  changeLayer?: LiveSummaryChangeLayer;
  changeLayerLabel?: string;
  before?: string | null;
  after?: string | null;
  severity?: 'INFO' | 'WARNING' | 'DANGER' | string;
  reason?: string;
  occurredAt?: string;
  eventScope?: 'PUBLIC' | 'USER' | string;
  entityType?: 'SECTOR' | 'HOLDING' | 'STOCK' | 'ISSUE' | string;
  entityKey?: string;
  title?: string;
  changeValue?: number | null;
  payload?: Record<string, unknown> | string | null;
};

export type LiveSummaryChangeLayers = {
  contractVersion?: 'CHANGE_LAYERS_V1' | string;
  factChanges?: LiveSummaryChangeItem[];
  interpretationChanges?: LiveSummaryChangeItem[];
  judgmentChanges?: LiveSummaryChangeItem[];
  factCount?: number;
  interpretationCount?: number;
  judgmentCount?: number;
};

export type LiveSummaryChangesResponse = {
  window?: 'SINCE_YESTERDAY_KST' | string;
  timezone?: string;
  sinceAt?: string;
  generatedAt?: string;
  count?: number;
  changes?: LiveSummaryChangeItem[];
  changeLayers?: LiveSummaryChangeLayers;
};

export function buildLiveSummaryChangeDeepLink(
    change: Pick<LiveSummaryChangeItem, 'eventId' | 'entityType' | 'entityKey'>,
): string {
  const entityType = String(change?.entityType ?? '').trim().toUpperCase();
  const entityKey = String(change?.entityKey ?? '').trim();
  const eventId = String(change?.eventId ?? '').trim();

  const tab = entityType === 'HOLDING'
      ? 'HOLDINGS'
      : entityType === 'STOCK'
          ? 'STOCKS'
          : entityType === 'ISSUE'
              ? 'ISSUES'
              : entityType === 'SECTOR'
                  ? 'SECTORS'
                  : 'ALERTS';

  const params = new URLSearchParams();
  params.set('tab', tab);
  if (eventId) params.set('changeEventId', eventId);
  if (entityType) params.set('changeEntityType', entityType);
  if (entityKey) params.set('changeEntityKey', entityKey);

  return `/live-summary-mini?${params.toString()}`;
}

export type LiveSummaryApiResponse = {
  marketState?: LiveSummaryMarketState;
  marketContext?: LiveSummaryMarketContext;
  liveIssueSignal?: LiveSummaryLiveIssueSignal;
  opportunityRadar?: LiveSummaryOpportunity[];
  attentionBanner?: LiveSummaryAttentionBanner;
  strongSectors?: LiveSummarySector[];
  themeFlows?: LiveSummaryThemeFlow[];
  holdings?: LiveSummaryHolding[];
  alerts?: LiveSummaryAlert[];
  issues?: unknown[]; realtimeIssues?: unknown[]; changeEvents?: LiveSummaryChangeEvent[];
  access?: LiveSummaryAccess; sourceStatus?: LiveSummarySourceStatus | string;
  generatedAt?: string; marketSession?: 'PRE_MARKET' | 'MARKET_OPEN' | 'POST_MARKET' | string;
  marketOpen?: boolean; cacheTtlSeconds?: number; fixedPanel?: boolean;
  panelMode?: string; userResolved?: boolean;
  /** @deprecated marketContext로 대체합니다. */
  derivativeSignal?: LiveSummaryDerivativeSignal;
};

export type LiveSummaryHoldingSearchItem = {
  ticker: string;
  name: string;
  market?: string;
  alreadyAdded?: boolean;
};

export type LiveSummaryHoldingMutationAction =
    | 'CREATED'
    | 'RESTORED'
    | 'ALREADY_ACTIVE';

export type LiveSummaryHoldingMutationResult = {
  ticker: string;
  name?: string;
  active: boolean;
  /**
   * Legacy compatibility field.
   * true only when the server state changed (CREATED/RESTORED).
   */
  added: boolean;
  action: LiveSummaryHoldingMutationAction;
};

export type LiveSummaryIssueView = 'topic' | 'raw';

export type LiveSummaryIssueSectorFacet = {
  name: string;
  count: number;
};

export type LiveSummaryIssuesPage = {
  view?: LiveSummaryIssueView;
  topicAvailable?: boolean;
  fallbackReason?: string;
  items: unknown[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasPrevious: boolean;
  hasNext: boolean;
  query: string;
  selectedSector: string;
  sectorFacets: LiveSummaryIssueSectorFacet[];
};
export type GetLiveSummaryOptions = {
  phase?: string;
  timeoutMs?: number;
  refresh?: boolean;
  includeIssues?: boolean;
  signal?: AbortSignal;
};

export async function getLiveSummary(
    phaseOrOptions: string | GetLiveSummaryOptions = 'AUTO',
    legacyTimeoutMs = 5_000,
): Promise<LiveSummaryApiResponse> {
  const options: GetLiveSummaryOptions = typeof phaseOrOptions === 'string'
      ? {phase: phaseOrOptions, timeoutMs: legacyTimeoutMs}
      : phaseOrOptions;
  const res = await api.get<LiveSummaryApiResponse>('/live-summary', {
    params: {
      phase: options.phase ?? 'AUTO',
      refresh: options.refresh ?? false,
      includeIssues: options.includeIssues ?? true,
    },
    timeout: options.timeoutMs ?? 5_000, withCredentials: true, signal: options.signal,
  });
  return res.data;
}

export async function getLiveSummaryThemeLeaders(
    options?: { limit?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummaryThemeStock[]> {
  const res = await api.get<LiveSummaryThemeStock[]>('/live-summary/theme-leaders', {
    params: {limit: Math.max(1, Math.min(15, options?.limit ?? 15))},
    timeout: options?.timeoutMs ?? 7_000,
    withCredentials: true,
    signal: options?.signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}


export async function getLiveSummaryThemeStocks(
    themeCode: string,
    options?: { limit?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummaryThemeStock[]> {
  const normalizedThemeCode = themeCode.trim();
  if (!normalizedThemeCode) return [];

  const res = await api.get<LiveSummaryThemeStock[]>(
      `/live-summary/themes/${encodeURIComponent(normalizedThemeCode)}/stocks`,
      {
        params: {limit: Math.max(1, Math.min(100, options?.limit ?? 100))},
        timeout: options?.timeoutMs ?? 7_000,
        withCredentials: true,
        signal: options?.signal,
      },
  );
  return Array.isArray(res.data) ? res.data : [];
}


const LIVE_SUMMARY_ISSUE_CLIENT_CACHE_TTL_MS = 30_000;
const liveSummaryIssuePageCache = new Map<string, {
  savedAt: number;
  value: LiveSummaryIssuesPage
}>();
const liveSummaryIssueInFlight = new Map<string, Promise<LiveSummaryIssuesPage>>();

function liveSummaryIssueCacheKey(
    view: LiveSummaryIssueView,
    page: number,
    size: number,
    query: string,
    sector: string,
): string {
  return `${view}|${query.toLocaleLowerCase()}|${sector.toLocaleLowerCase()}|${page}|${size}`;
}

function readLiveSummaryIssueCache(key: string): LiveSummaryIssuesPage | null {
  const cached = liveSummaryIssuePageCache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.savedAt > LIVE_SUMMARY_ISSUE_CLIENT_CACHE_TTL_MS) {
    liveSummaryIssuePageCache.delete(key);
    return null;
  }
  return cached.value;
}

function writeLiveSummaryIssueCache(key: string, value: LiveSummaryIssuesPage): void {
  liveSummaryIssuePageCache.set(key, {savedAt: Date.now(), value});
  if (liveSummaryIssuePageCache.size > 48) {
    const oldest = liveSummaryIssuePageCache.keys().next().value;
    if (oldest) liveSummaryIssuePageCache.delete(oldest);
  }
}

export async function getLiveSummaryIssues(
    options?: {
      page?: number;
      size?: number;
      query?: string;
      sector?: string;
      view?: LiveSummaryIssueView;
      refresh?: boolean;
      preferCache?: boolean;
      timeoutMs?: number;
      signal?: AbortSignal;
    },
): Promise<LiveSummaryIssuesPage> {
  const page = Math.max(1, Math.trunc(options?.page ?? 1));
  const size = Math.max(1, Math.min(50, Math.trunc(options?.size ?? 30)));
  const query = String(options?.query ?? '').trim().slice(0, 120);
  const sector = String(options?.sector ?? '').trim().slice(0, 80);
  const view: LiveSummaryIssueView = options?.view === 'topic' ? 'topic' : 'raw';
  const refresh = options?.refresh ?? false;
  const preferCache = options?.preferCache ?? false;
  const cacheKey = liveSummaryIssueCacheKey(view, page, size, query, sector);

  if (!refresh && preferCache) {
    const cached = readLiveSummaryIssueCache(cacheKey);
    if (cached) return cached;

    // A background prefetch for this exact page may already be running. Reuse it
    // instead of firing a duplicate 1~2 second DB query when the user clicks quickly.
    const inFlight = liveSummaryIssueInFlight.get(cacheKey);
    if (inFlight) return inFlight;
  }

  const request = (async (): Promise<LiveSummaryIssuesPage> => {
    const res = await api.get<LiveSummaryIssuesPage | unknown[]>('/live-summary/issues', {
      params: {
        page,
        size,
        query,
        sector,
        view,
        refresh,
      },
      timeout: options?.timeoutMs ?? 15_000,
      withCredentials: true,
      signal: options?.signal,
    });

    // Staggered-deploy compatibility: an older backend returned the raw array.
    let normalized: LiveSummaryIssuesPage;
    if (Array.isArray(res.data)) {
      normalized = {
        view: 'raw',
        topicAvailable: false,
        fallbackReason: 'LEGACY_ARRAY_RESPONSE',
        items: res.data,
        page: 1,
        size,
        totalElements: res.data.length,
        totalPages: res.data.length ? 1 : 0,
        hasPrevious: false,
        hasNext: false,
        query: '',
        selectedSector: '',
        sectorFacets: [],
      };
    } else {
      const data = res.data ?? ({} as LiveSummaryIssuesPage);
      normalized = {
        view: data.view === 'raw' ? 'raw' : 'topic',
        topicAvailable: data.topicAvailable !== false,
        fallbackReason: String(data.fallbackReason ?? ''),
        items: Array.isArray(data.items) ? data.items : [],
        page: Math.max(1, Math.trunc(Number(data.page) || page)),
        size: Math.max(1, Math.trunc(Number(data.size) || size)),
        totalElements: Math.max(0, Math.trunc(Number(data.totalElements) || 0)),
        totalPages: Math.max(0, Math.trunc(Number(data.totalPages) || 0)),
        hasPrevious: Boolean(data.hasPrevious),
        hasNext: Boolean(data.hasNext),
        query: String(data.query ?? query),
        selectedSector: String(data.selectedSector ?? sector).trim(),
        sectorFacets: Array.isArray(data.sectorFacets)
            ? data.sectorFacets
            .map((facet) => ({
              name: String((facet as LiveSummaryIssueSectorFacet)?.name ?? '').trim(),
              count: Math.max(0, Math.trunc(Number((facet as LiveSummaryIssueSectorFacet)?.count) || 0)),
            }))
            .filter((facet) => facet.name && facet.count > 0)
            : [],
      };
    }

    if (!refresh) writeLiveSummaryIssueCache(cacheKey, normalized);
    return normalized;
  })();

  if (!refresh && preferCache) liveSummaryIssueInFlight.set(cacheKey, request);
  try {
    return await request;
  } finally {
    if (liveSummaryIssueInFlight.get(cacheKey) === request) {
      liveSummaryIssueInFlight.delete(cacheKey);
    }
  }
}

/**
 * Warm one numbered issue page without touching UI state. The regular page click can
 * then resolve from the same in-memory promise/cache immediately. Failures are ignored
 * because prefetch is only a latency optimization.
 */
export async function prefetchLiveSummaryIssues(
    options: {
      page: number;
      size?: number;
      query?: string;
      sector?: string;
      view?: LiveSummaryIssueView;
      timeoutMs?: number
    },
): Promise<void> {
  try {
    await getLiveSummaryIssues({
      page: options.page,
      size: options.size,
      query: options.query,
      sector: options.sector,
      view: options.view,
      refresh: false,
      preferCache: true,
      timeoutMs: options.timeoutMs ?? 15_000,
    });
  } catch {
    // Best effort only. The visible request will retry normally on click.
  }
}

export async function getLiveSummaryDecisionEvents(
    options?: {
      limit?: number;
      refresh?: boolean;
      timeoutMs?: number;
      signal?: AbortSignal;
    },
): Promise<unknown[]> {
  const res = await api.get<unknown[]>('/live-summary/issues/decision-events', {
    params: {
      limit: Math.max(1, Math.min(20, Math.trunc(options?.limit ?? 10))),
      refresh: options?.refresh ?? false,
    },
    timeout: options?.timeoutMs ?? 12_000,
    withCredentials: true,
    signal: options?.signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}


export async function getLiveSummaryHoldings(
    options?: { refresh?: boolean; timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummaryHolding[]> {
  const res = await api.get<LiveSummaryHolding[]>('/live-summary/holdings', {
    params: {refresh: options?.refresh ?? false},
    timeout: options?.timeoutMs ?? 12_000,
    withCredentials: true,
    signal: options?.signal,
  });
  if (!Array.isArray(res.data)) {
    throw new Error('감시종목 목록 API 응답이 배열 형식이 아닙니다.');
  }
  return res.data;
}

export async function getLiveSummaryChangeEvents(
    options?: { limit?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummaryChangeEvent[]> {
  const res = await api.get<LiveSummaryChangeEvent[]>('/live-summary/events', {
    params: {limit: options?.limit ?? 30}, timeout: options?.timeoutMs ?? 5_000,
    withCredentials: true, signal: options?.signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function getLiveSummaryChanges(
    options?: { limit?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummaryChangesResponse> {
  const res = await api.get<LiveSummaryChangesResponse>('/live-summary/changes', {
    params: {limit: Math.max(1, Math.min(100, options?.limit ?? 30))},
    timeout: options?.timeoutMs ?? 5_000,
    withCredentials: true,
    signal: options?.signal,
  });
  return res.data ?? {window: 'SINCE_YESTERDAY_KST', changes: []};
}

export async function getLiveSummarySignalHistory(
    ticker: string,
    eventType?: string,
    options?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummarySignalHistoryStats> {
  const normalizedTicker = ticker.trim();
  if (!/^\d{6}$/.test(normalizedTicker)) {
    return {ticker: normalizedTicker, available: false, horizons: {}};
  }
  const res = await api.get<LiveSummarySignalHistoryStats>(
      `/live-summary/stocks/${encodeURIComponent(normalizedTicker)}/signal-history`,
      {
        params: eventType ? {eventType} : undefined,
        timeout: options?.timeoutMs ?? 5_000,
        withCredentials: true,
        signal: options?.signal,
      },
  );
  return res.data ?? {ticker: normalizedTicker, available: false, horizons: {}};
}

export async function searchLiveSummaryHoldings(
    keyword: string,
    options?: { limit?: number; timeoutMs?: number; signal?: AbortSignal },
): Promise<LiveSummaryHoldingSearchItem[]> {
  const normalized = keyword.trim();
  if (!normalized) return [];
  const res = await api.get<LiveSummaryHoldingSearchItem[]>('/live-summary/holdings/search', {
    params: {keyword: normalized, limit: options?.limit ?? 10},
    timeout: options?.timeoutMs ?? 5_000, withCredentials: true, signal: options?.signal,
  });
  return Array.isArray(res.data) ? res.data : [];
}

export async function addLiveSummaryHolding(
    ticker: string,
): Promise<LiveSummaryHoldingMutationResult> {
  const res = await api.post<Partial<LiveSummaryHoldingMutationResult>>(
      '/live-summary/holdings',
      {ticker},
      {timeout: 5_000, withCredentials: true, headers: {'Content-Type': 'application/json'}},
  );

  const raw = res.data ?? {};
  const action: LiveSummaryHoldingMutationAction =
      raw.action === 'CREATED' || raw.action === 'RESTORED' || raw.action === 'ALREADY_ACTIVE'
          ? raw.action
          : raw.added === true
              ? 'CREATED'
              : 'ALREADY_ACTIVE';

  return {
    ticker: String(raw.ticker ?? ticker),
    name: raw.name,
    active: raw.active !== false,
    added: action === 'CREATED' || action === 'RESTORED',
    action,
  };
}

export async function removeLiveSummaryHolding(ticker: string): Promise<void> {
  await api.delete(`/live-summary/holdings/${encodeURIComponent(ticker)}`, {
    timeout: 5_000, withCredentials: true,
  });
}

